import { createSelector } from "@reduxjs/toolkit";

import type { RootState } from "newui/store";

import type { FeatureFlagType } from "./types/FeatureFlag.type";
import { isNotNullOrUndefined } from "newui/utils/objectUtil";
import { USER_ROLE_NAMES } from "./constants/UserRoleConstants";

// Basic selectors
export const selectAuth = (state: RootState) => state.auth;
export const selectUser = (state: RootState) => state.auth.user ?? null;
export const selectCurrentUserId = createSelector(
	[selectUser],
	(user) => user?.id ?? null
);
export const selectUserFeatureFlags = (state: RootState) =>
	state.auth.user_feature_flags;
export const selectUserRoles = (state: RootState) => state.auth.user_roles;
export const selectCurrentCompany = (state: RootState) =>
	state.auth.currentCompany;
export const selectCurrentProfileType = (state: RootState) =>
	state.auth.currentProfileType;
export const selectCurrentCompanyId = createSelector(
	[selectCurrentCompany],
	(company) => company?.id ?? null
);
export const selectSignupDetails = (state: RootState) =>
	state.auth.signup_details;
export const selectTwoFactorEnabled = (state: RootState) =>
	state.auth.two_factor_enabled;
export const selectTwoFactorVerified = (state: RootState) =>
	state.auth.two_factor_verified;
export const selectAuthLoading = (state: RootState) => state.auth.is_loading;
export const selectProfileLoading = (state: RootState) =>
	state.auth.is_profile_loading;
export const selectGuestSwitch = (state: RootState) => state.auth.guestSwitch;
export const selectReferralToken = (state: RootState) =>
	state.auth.referralToken;

// Memoized/computed selectors
export const selectIsAuthenticated = createSelector([selectUser], () =>
	Boolean(isNotNullOrUndefined(selectUser))
);

export const selectIsGuestLoggedIn = createSelector([selectUser], (user) => {
	if (!user || !Array.isArray(user.roles)) {
		return false;
	}

	return user.roles.some(
		(role) => role?.name?.toLowerCase() === USER_ROLE_NAMES.GUEST.toLowerCase()
	);
});

export const selectIsGuestSwitchLoaded = createSelector(
	[selectGuestSwitch],
	(guestSwitch) => guestSwitch.is_loaded
);

export const selectHasFeature = (featureFlag: FeatureFlagType) =>
	createSelector([selectUserFeatureFlags], (flags) =>
		flags.includes(featureFlag)
	);

export const selectHasRole = (roleName: string) =>
	createSelector([selectUserRoles], (roles) => roles.includes(roleName));
